// ─── Domain Realism Profiles ──────────────────────────────────────────────────
// Empirically-grounded domain presets.
// Each profile shapes effect sizes, variance, contradiction rates, and
// methodological expectations so generated research matches disciplinary norms.

export const DOMAIN_PROFILES = {
  public_policy: {
    label: 'Public Policy / Governance',
    emoji: '🏛',
    effectSizes:           { strong: 0.40, moderate: 0.26, weak: 0.11 },
    typicalR2Range:        [0.12, 0.42],
    significanceRate:      0.42,   // ~42% of predictors significant — policy is messy
    contradictionLikelihood: 0.65,
    qualitativeTensionLevel: 'high',
    analysisSophistication: 'moderate',
    typicalSampleRange:    [60, 250],
    institutionalEfficiency: 0.38,
    diminishingReturns:    true,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols'],
    avoidAnalyses:         ['factor'], // factor analysis rarely justified for governance
    contradictions: [
      'Higher public expenditure did not consistently improve operational efficiency',
      'Regulatory compliance rates improved but real-world outcomes remained stagnant',
      'Governance quality indices improved transparency while service delivery deteriorated',
      'Policy adoption was high but implementation fidelity remained weak',
      'Formal institutional capacity was strong yet informal sector dynamics dominated',
    ],
    dominantNarratives: [
      'Policy-implementation gap: rules exist but enforcement is selective',
      'Isomorphic mimicry: organisations adopt reform language without functional change',
      'Bureaucratic capture: reform agendas absorbed by incumbent interests',
      'Decoupling: reported metrics diverge from ground-level realities',
    ],
    qualitativeThemes: [
      'Operational dysfunction despite positive institutional metrics',
      'Frontline worker contradictions: "we follow guidelines, but nothing changes"',
      'Corruption concerns persist under formal accountability regimes',
      'Resource allocation is technically correct but practically ineffective',
    ],
  },

  marketing: {
    label: 'Marketing / Consumer Behaviour',
    emoji: '📣',
    effectSizes:           { strong: 0.50, moderate: 0.36, weak: 0.16 },
    typicalR2Range:        [0.22, 0.58],
    significanceRate:      0.60,
    contradictionLikelihood: 0.35,
    qualitativeTensionLevel: 'low',
    analysisSophistication: 'moderate',
    typicalSampleRange:    [150, 500],
    institutionalEfficiency: 0.72,
    diminishingReturns:    false,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'cronbach'],
    avoidAnalyses:         [],
    contradictions: [
      'Customer engagement improved brand loyalty but did not increase repeat purchase frequency',
      'Digital advertising spend increased brand awareness without proportional sales uplift',
      'Satisfaction scores were high but switching intention remained significant',
    ],
    dominantNarratives: [
      'Attitude-behaviour gap: positive attitudes do not reliably predict purchase',
      'Engagement-revenue paradox: high engagement does not always monetise',
      'Brand equity as a lagged, not contemporaneous, purchase driver',
    ],
    qualitativeThemes: [
      'Consumers articulate strong brand affinity but rationalise competitor purchases situationally',
      'Loyalty programme members demonstrate transactional, not emotional, commitment',
      'Digital engagement is habitual rather than intentional',
    ],
  },

  education: {
    label: 'Education / Learning Science',
    emoji: '🎓',
    effectSizes:           { strong: 0.44, moderate: 0.30, weak: 0.12 },
    typicalR2Range:        [0.14, 0.48],
    significanceRate:      0.48,
    contradictionLikelihood: 0.55,
    qualitativeTensionLevel: 'medium',
    analysisSophistication: 'moderate',
    typicalSampleRange:    [80, 400],
    institutionalEfficiency: 0.52,
    diminishingReturns:    true,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'cronbach', 'anova'],
    avoidAnalyses:         [],
    contradictions: [
      'Per-student expenditure above a threshold showed diminishing returns on attainment',
      'Infrastructure investment improved learning environment but not test outcomes',
      'Teacher qualifications were significant predictors but teacher experience was not',
      'Parental involvement moderated outcomes in unexpected, domain-specific ways',
    ],
    dominantNarratives: [
      'Inputs do not guarantee outcomes without pedagogical quality mediating the relationship',
      'Structural inequality operates independently of institutional investment',
      'Teacher agency moderates policy effectiveness at the classroom level',
    ],
    qualitativeThemes: [
      'Teachers describe resource improvements that do not translate to classroom practice',
      'Students report high institutional satisfaction alongside persistent learning gaps',
      'Parents note visible infrastructure changes but unchanged educational culture',
    ],
  },

  healthcare: {
    label: 'Healthcare / Public Health',
    emoji: '🏥',
    effectSizes:           { strong: 0.46, moderate: 0.32, weak: 0.13 },
    typicalR2Range:        [0.18, 0.52],
    significanceRate:      0.52,
    contradictionLikelihood: 0.48,
    qualitativeTensionLevel: 'medium',
    analysisSophistication: 'advanced',
    typicalSampleRange:    [100, 600],
    institutionalEfficiency: 0.58,
    diminishingReturns:    false,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'cronbach', 'anova'],
    avoidAnalyses:         [],
    contradictions: [
      'Health facility availability improved access but did not reduce disease burden proportionally',
      'Patient satisfaction and clinical outcomes were weakly correlated',
      'Community health worker deployment showed strong urban effects but weak rural ones',
    ],
    dominantNarratives: [
      'Structural determinants of health outweigh clinical intervention effects',
      'Supply-side expansion without demand-side enablement yields weak outcomes',
      'Quality and quantity of care operate through distinct causal pathways',
    ],
    qualitativeThemes: [
      'Patients describe physical proximity to facilities but persistent cultural barriers',
      'Health workers report protocol adherence without meaningful community trust',
      'Administrators cite improved metrics under conditions staff describe as overcrowded',
    ],
  },

  finance: {
    label: 'Finance / Accounting',
    emoji: '💹',
    effectSizes:           { strong: 0.54, moderate: 0.40, weak: 0.18 },
    typicalR2Range:        [0.20, 0.65],
    significanceRate:      0.62,
    contradictionLikelihood: 0.28,
    qualitativeTensionLevel: 'low',
    analysisSophistication: 'advanced',
    typicalSampleRange:    [50, 300],
    institutionalEfficiency: 0.75,
    diminishingReturns:    false,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'moderation'],
    avoidAnalyses:         [],
    contradictions: [
      'Leverage ratio was significant in cross-sectional analysis but not in fixed effects models',
      'Profitability metrics diverged across GAAP and IFRS reporting frameworks',
      'Dividend policy significance varied materially by firm size quartile',
    ],
    dominantNarratives: [
      'Market efficiency is conditional, not universal',
      'Accounting quality moderates the relevance of financial ratios to firm value',
    ],
    qualitativeThemes: [
      'CFOs describe earnings management as risk mitigation, not manipulation',
      'Analysts cite model limitations that formal reports systematically understate',
    ],
  },

  human_resources: {
    label: 'Human Resources / OB',
    emoji: '👥',
    effectSizes:           { strong: 0.48, moderate: 0.34, weak: 0.14 },
    typicalR2Range:        [0.18, 0.52],
    significanceRate:      0.55,
    contradictionLikelihood: 0.42,
    qualitativeTensionLevel: 'medium',
    analysisSophistication: 'moderate',
    typicalSampleRange:    [100, 400],
    institutionalEfficiency: 0.62,
    diminishingReturns:    false,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'cronbach', 'anova'],
    avoidAnalyses:         [],
    contradictions: [
      'Job satisfaction did not significantly predict turnover intention when controlling for job embeddedness',
      'Performance appraisal fairness perceptions improved motivation but not objective performance',
      'Training investment showed lagged, not immediate, productivity effects',
    ],
    dominantNarratives: [
      'Employee attitudes are proximal predictors but distal outcomes are multi-determined',
      'Organisational justice moderates the relationship between HR practices and commitment',
    ],
    qualitativeThemes: [
      'Employees report perceived fairness while simultaneously describing structural inequities',
      'Managers endorse HR policies that frontline staff experience as performative',
    ],
  },

  sustainability: {
    label: 'Sustainability / Environment',
    emoji: '🌱',
    effectSizes:           { strong: 0.42, moderate: 0.28, weak: 0.11 },
    typicalR2Range:        [0.12, 0.44],
    significanceRate:      0.44,
    contradictionLikelihood: 0.60,
    qualitativeTensionLevel: 'high',
    analysisSophistication: 'moderate',
    typicalSampleRange:    [60, 250],
    institutionalEfficiency: 0.42,
    diminishingReturns:    true,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols'],
    avoidAnalyses:         [],
    contradictions: [
      'Environmental reporting scores were high while carbon emissions remained elevated',
      'Green investment improved ESG ratings without proportional operational improvement',
      'Sustainability certification adoption preceded, rather than caused, emission reductions',
    ],
    dominantNarratives: [
      'Greenwashing: symbolic compliance without substantive transition',
      'Measurement gaming: reported metrics decouple from physical environmental outcomes',
    ],
    qualitativeThemes: [
      'Managers describe genuine sustainability commitment under structural economic pressures preventing it',
      'Employees distinguish between certified sustainability and perceived authentic commitment',
    ],
  },

  operations: {
    label: 'Operations / Supply Chain',
    emoji: '⚙️',
    effectSizes:           { strong: 0.52, moderate: 0.38, weak: 0.16 },
    typicalR2Range:        [0.22, 0.60],
    significanceRate:      0.58,
    contradictionLikelihood: 0.32,
    qualitativeTensionLevel: 'low',
    analysisSophistication: 'moderate',
    typicalSampleRange:    [80, 300],
    institutionalEfficiency: 0.70,
    diminishingReturns:    false,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'anova'],
    avoidAnalyses:         [],
    contradictions: [
      'Lean implementation improved process efficiency but increased workforce stress indicators',
      'Technology adoption improved throughput while increasing dependency and fragility',
    ],
    dominantNarratives: [
      'Efficiency-resilience trade-off: lean systems are vulnerable to disruption',
      'Technology adoption creates new interdependencies alongside productivity gains',
    ],
    qualitativeThemes: [
      'Operators describe process improvements that eliminated tacit knowledge buffers',
      'Managers acknowledge efficiency gains alongside deteriorating workforce morale',
    ],
  },

  technology_adoption: {
    label: 'Technology Adoption / Digital',
    emoji: '💻',
    effectSizes:           { strong: 0.52, moderate: 0.38, weak: 0.16 },
    typicalR2Range:        [0.25, 0.62],
    significanceRate:      0.60,
    contradictionLikelihood: 0.30,
    qualitativeTensionLevel: 'low',
    analysisSophistication: 'advanced',
    typicalSampleRange:    [150, 500],
    institutionalEfficiency: 0.72,
    diminishingReturns:    false,
    recommendedAnalyses:   ['descriptive', 'correlation', 'ols', 'cronbach', 'moderation'],
    avoidAnalyses:         [],
    contradictions: [
      'Perceived usefulness strongly predicted intention but not actual adoption behaviour',
      'System quality improvements showed weaker effects in low-digital-literacy subgroups',
    ],
    dominantNarratives: [
      'Intention-behaviour gap: TAM models explain intention better than actual use',
      'Digital divide moderates technology adoption relationships in heterogeneous samples',
    ],
    qualitativeThemes: [
      'Users express positive technology evaluations while maintaining legacy workarounds',
      'IT managers describe adoption success metrics that exclude sustained usage patterns',
    ],
  },
}

export function getDomainProfile(domain) {
  return DOMAIN_PROFILES[domain] || DOMAIN_PROFILES.public_policy
}

export const DOMAIN_KEYS = Object.keys(DOMAIN_PROFILES)
